/**
 * PCI Proxy Secure Fields script loader for Option B (Payment Card Authorization).
 *
 * Loads the Datatrans Secure Fields script once; environment (sandbox vs production)
 * is chosen from VITE_MEWS_API_BASE_URL (mews-demo → sandbox, else production).
 *
 * @see STEP_4_4_OPTION_B_TASKMASTER.md - Task 4
 * @see supporting-payment-cards.md - PCI Proxy Secure Fields URLs
 */

const PCI_PROXY_SCRIPT_ID = 'pci-proxy-secure-fields-script';
const SANDBOX_URL = 'https://pay.sandbox.datatrans.com/upp/payment/js/secure-fields-2.0.0.min.js';
const PRODUCTION_URL = 'https://pay.datatrans.com/upp/payment/js/secure-fields-2.0.0.min.js';

let loadPromise: Promise<void> | null = null;

/**
 * Returns the PCI Proxy Secure Fields script URL for the current environment.
 * Uses VITE_MEWS_API_BASE_URL: if it contains "mews-demo", returns sandbox URL; else production.
 */
export function getPciProxySecureFieldsScriptUrl(): string {
  const apiBaseUrl = import.meta.env.VITE_MEWS_API_BASE_URL ?? '';
  if (apiBaseUrl.includes('mews-demo')) {
    return SANDBOX_URL;
  }
  return PRODUCTION_URL;
}

/**
 * Loads the PCI Proxy Secure Fields script dynamically. Resolves when the script has loaded.
 * Subsequent calls return the same Promise; the script is only injected once.
 */
export function loadPciProxySecureFieldsScript(): Promise<void> {
  if (loadPromise !== null) {
    return loadPromise;
  }

  const existing = document.getElementById(PCI_PROXY_SCRIPT_ID);
  if (existing && existing instanceof HTMLScriptElement && existing.src) {
    loadPromise = Promise.resolve();
    return loadPromise;
  }

  loadPromise = new Promise<void>((resolve, reject) => {
    const src = getPciProxySecureFieldsScriptUrl();
    const script = document.createElement('script');
    script.id = PCI_PROXY_SCRIPT_ID;
    script.src = src;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      loadPromise = null;
      reject(new Error(`Failed to load PCI Proxy Secure Fields script: ${src}`));
    };
    document.head.appendChild(script);
  });

  return loadPromise;
}
